import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, List, Card, message } from 'antd';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import api from '../../services/api';

const LocationSearch = ({ value, onChange, placeholder = "Enter venue address or area", size = "large", status }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(null);
    const debounceRef = useRef(null);
    const inputRef = useRef(null);

    // Debounced search function
    const searchLocations = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setLoading(true);
        try {
            // Call backend proxy to avoid CSP/CORS
            const response = await api.searchLocations(query, { limit: 5, countrycodes: 'in' });
            
            // Handle both array and object responses
            const data = Array.isArray(response) ? response : (response.data || []);
            
            const formattedSuggestions = data.map(item => ({
                id: item.place_id,
                display_name: item.display_name,
                lat: parseFloat(item.lat),
                lon: parseFloat(item.lon),
                address: {
                    city: item.address?.city || item.address?.town || item.address?.village,
                    state: item.address?.state,
                    country: item.address?.country,
                    postcode: item.address?.postcode
                }
            }));
            
            setSuggestions(formattedSuggestions);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Location search error:', error);
            message.error('Failed to search locations');
        } finally {
            setLoading(false);
        }
    };

    // Handle input change with debouncing
    const handleInputChange = (e) => {
        const query = e.target.value;
        onChange(query);
        
        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        
        // Set new debounce
        debounceRef.current = setTimeout(() => {
            searchLocations(query);
        }, 500);
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestion) => {
        onChange(suggestion.display_name);
        setShowSuggestions(false);
        setSuggestions([]);
    };

    // Get current location
    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            message.error('Geolocation is not supported by this browser');
            return;
        }

        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                
                try {
                    // Reverse geocoding via backend proxy
                    const response = await fetch(
                        `/api/geo/reverse?lat=${latitude}&lon=${longitude}`
                    );
                    const data = await response.json();
                    
                    if (data && data.display_name) {
                        setCurrentLocation(data.display_name);
                        onChange(data.display_name);
                        message.success('Current location detected');
                    }
                } catch (error) {
                    console.error('Reverse geocoding error:', error);
                    message.error('Failed to get current location address');
                } finally {
                    setLoading(false);
                }
            },
            (error) => {
                setLoading(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        message.error('Location access denied by user');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        message.error('Location information is unavailable');
                        break;
                    case error.TIMEOUT:
                        message.error('Location request timed out');
                        break;
                    default:
                        message.error('An unknown error occurred while getting location');
                        break;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes
            }
        );
    };

    // Hide suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="relative" ref={inputRef}>
            <Input.Group compact>
                <Input
                    placeholder={placeholder}
                    value={value}
                    onChange={handleInputChange}
                    size={size}
                    status={status}
                    prefix={<EnvironmentOutlined />}
                    loading={loading}
                    style={{ width: 'calc(100% - 40px)' }}
                />
                <Button
                    icon={<AimOutlined />}
                    onClick={getCurrentLocation}
                    loading={loading}
                    size={size}
                    title="Use current location"
                    style={{ width: '40px' }}
                />
            </Input.Group>
            
            {showSuggestions && suggestions.length > 0 && (
                <Card 
                    className="absolute top-full left-0 right-0 z-50 mt-1 shadow-lg"
                    bodyStyle={{ padding: 0 }}
                >
                    <List
                        size="small"
                        dataSource={suggestions}
                        renderItem={(item) => (
                            <List.Item
                                className="cursor-pointer hover:bg-gray-50 px-3 py-2"
                                onClick={() => handleSuggestionSelect(item)}
                            >
                                <div className="flex items-start">
                                    <EnvironmentOutlined className="text-orange-500 mt-1 mr-2 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                            {item.address.city || 'Unknown City'}
                                            {item.address.state && `, ${item.address.state}`}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                            {item.display_name}
                                        </div>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            )}
        </div>
    );
};

export default LocationSearch;
